import { useReducer } from "react";
import biApi from "../../../../../../api/biApi";
import type { SmartViewWidget } from "../../../../../../api/types/dashboardsTypes.ts";
import type { PivotReportConfigurationForRunning } from "../../../../../../api/types/svTypes.ts";
import DrillDownEntry from "../../../../reporting/sv/components/builder/common/drillDown/DrillDownEntry.tsx";
import useViewReportHandlers from "../../../../reporting/sv/components/viewer/common/hooks/useViewReportHandlers.ts";
import { initializePivotRunState, reducer } from "../../../../reporting/sv/components/viewer/storage/runState.ts";
import { isPivotRunState } from "../../../../reporting/sv/components/viewer/storage/runStateUtils.ts";
import { BiCommonDataContextProvider } from "../../../../reporting/sv/contexts/BiCommonDataContextProvider.tsx";
import type { DrillDownArgs } from "../DrillDownArgs.ts";
import SmartViewWidgetWrapper from "../SmartViewWidgetWrapper";
import SmartViewPivotWidgetViewer from "./SmartViewPivotWidgetViewer";

interface Props {
  widget: SmartViewWidget;
  data: PivotReportConfigurationForRunning;
  onDrillDown: (args: DrillDownArgs) => void;
}

const SmartViewPivotContainer = ({ data, widget, onDrillDown }: Props) => {
  const [runState, dispatchRunState] = useReducer(reducer, initializePivotRunState(data, widget.reportId));

  const { handleConditionUpdate, handleFieldUpdate, handleRefreshGrid, handleSortingUpdate } =
    useViewReportHandlers(dispatchRunState);

  if (!isPivotRunState(runState)) {
    return null;
  }

  return (
    <BiCommonDataContextProvider
      reportType={runState.reportType}
      books={runState.books}
      isMultiBook={runState.isMultiBook}
      dataSetId={runState.dataSetId}
      dimensionsStructure={undefined}
      dimensions={runState.dimensions}
      editMode={false}
      conditions={runState.conditions}
      reportId={widget.reportId}
      refreshToken={runState.refreshToken}
      dimensionNames={runState.dimensionNames}
      measureNames={runState.measureNames}
      onConditionUpdate={handleConditionUpdate}
      onRefreshGrid={handleRefreshGrid}
      onFieldWithCustomConditionUpdate={handleFieldUpdate}
    >
      <SmartViewWidgetWrapper widget={widget} conditions={data.conditions}>
        <SmartViewPivotWidgetViewer
          runState={runState}
          runReport={biApi.dashboardReports.runPivotReport}
          onDrillDown={onDrillDown}
          onSortingUpdate={handleSortingUpdate}
        />
      </SmartViewWidgetWrapper>
      <DrillDownEntry reportId={widget.reportId} />
    </BiCommonDataContextProvider>
  );
};

export default SmartViewPivotContainer;
